// El PIE de la columna de configuración de un modo: los ajustes que afinan la salida
// (Calidad · Formato), la cantidad y el Generar —
// ADR-2026-09-06-el-pie-de-configuracion-es-un-componente.
//
// Nació en el modo Modelo (ADR-2026-08-31-modelo-v2-ajustes-y-generar-fijos-abajo) y
// vive acá desde que lo usan dos columnas distintas: `PanelCompacto` (Modelo) y
// `PanelCatalogoCompacto` (Fotos de producto). Extraerlo y dejar la copia vieja en su
// lugar habría sido el mismo error que separó a los dos forks del sheet
// (ADR-2026-09-06-constantes-de-layout-compartidas-entre-los-dos-sheets): el pie está
// acá y NADIE más lo dibuja.
//
// Es el pie, no el layout: quien lo monta tiene que ser una columna en dos piezas
// —scroller `flex-1 min-h-0` arriba y esto abajo— para que quede fijo. El componente
// pone su `shrink-0`, su fondo y su línea, pero no puede fijarse solo.

import React from 'react';
import {
  CalidadSegmentada,
  CeldaAjuste,
  ValorConChevron,
  type CalidadImagen,
} from './AjustesRapidosProducto';
import { ContadorImagenes } from './ContadorImagenes';

export interface PieConfiguracionModoProps {
  /**
   * Calidad. OPCIONAL: sin las dos props la celda no se dibuja. Un modo puede no
   * ofrecerla en la columna —lo hizo Fotos de producto entre
   * ADR-2026-09-04-fotos-de-producto-sin-calidad-en-la-columna y
   * ADR-2026-09-06-el-pie-de-configuracion-es-un-componente— y el estado sigue viajando
   * al backend con su default.
   */
  calidad?: CalidadImagen;
  onCalidadChange?: (v: CalidadImagen) => void;
  /** Formato. OPCIONAL, misma regla que Calidad. */
  formatoLabel?: string;
  onOpenFormato?: () => void;
  /**
   * El contador de cantidad, PEGADO al Generar: es el único ajuste que cambia lo que
   * dice el botón —"Generar 3 imágenes"— y lo que cuesta la tanda, así que se decide con
   * el número y el precio a la vista, y no entre Calidad y Formato.
   *
   * Sin él el botón toma el ancho entero. Los modos cuya tanda es una CUENTA (Fotos de
   * producto: producto × vista) no tienen nada que contar acá.
   */
  contador?: {
    valor: number;
    max: number;
    onChange: (v: number) => void;
  };
  /** Cuántas imágenes anuncia el botón cuando no hay `ctaLabel`. */
  imagenesDeLaTanda: number;
  /** Reemplaza el texto del botón — el paso que no genera todavía ("Revisar catálogo"). */
  ctaLabel?: string;
  /** Créditos de la tanda: la pastilla al lado del texto. `null` o 0 no la dibuja. */
  creditCost?: number | null;
  onGenerate: () => void;
  /** Qué falta para poder generar: apaga el botón y se dibuja debajo. */
  falta?: string | null;
  /**
   * El mismo renglón, cuando no falta nada (de dónde sale el número de la tanda).
   *
   * `ReactNode` y no `string` porque `PanelCatalogoCompacto` ya la pasa así: la bajada
   * del review del Catálogo lleva marcado adentro.
   */
  nota?: React.ReactNode;
}

export const PieConfiguracionModo: React.FC<PieConfiguracionModoProps> = ({
  calidad,
  onCalidadChange,
  formatoLabel,
  onOpenFormato,
  contador,
  imagenesDeLaTanda,
  ctaLabel,
  creditCost,
  onGenerate,
  falta,
  nota,
}) => {
  const hayCalidad = calidad != null && !!onCalidadChange;
  const hayFormato = !!formatoLabel && !!onOpenFormato;
  const celdas = (hayCalidad ? 1 : 0) + (hayFormato ? 1 : 0);
  const mensaje = falta ?? nota;

  return (
    /* Fondo blanco propio y una línea arriba: el contenido de la columna le pasa por
       debajo al scrollear, y sin el fondo se veía a través. */
    <div className="shrink-0 flex flex-col gap-3 pt-3 bg-white border-t border-black/[0.06]">
      {/* ── Ajustes ──────────────────────────────────────────────────────
          En UNA fila, no apilados: son los que afinan la salida —no la definen— y tres
          filas de 48px pesaban tanto como las cards de Modelo, Pose y Fondo, que sí son
          decisiones. En fila ocupan un renglón y se leen de un vistazo.

          Las CELDAS son las de `AjustesRapidosProducto`, las mismas que dibuja
          Automático (ADR-2026-08-28-los-ajustes-se-ven-igual-en-los-tres-modos).

          Con una sola celda toma el ancho entero: media celda vacía al lado se lee como
          un control que falta cargar. */}
      {celdas > 0 && (
        <div className={`grid gap-2 ${celdas === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
          {hayCalidad && (
            <CeldaAjuste etiqueta="Calidad">
              <CalidadSegmentada calidad={calidad} onChange={onCalidadChange} />
            </CeldaAjuste>
          )}
          {/* Con chevron, como el resto de las celdas que abren un picker: sin él,
              Formato se veía igual que Calidad —que resuelve ahí mismo— y no avisaba que
              abre otra capa. */}
          {hayFormato && (
            <CeldaAjuste etiqueta="Formato" onClick={onOpenFormato}>
              <ValorConChevron texto={formatoLabel} />
            </CeldaAjuste>
          )}
        </div>
      )}

      {/* ── Generar ────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-2">
        <div className="flex items-stretch gap-2">
          {/* `[&>span]:mt-0`: `ContadorImagenes` trae un `mt-1` propio —viene de cuando
              vivía debajo de una etiqueta— y acá, en una caja centrada, esos 4px lo
              dejaban 10px del techo contra 6 del piso. Se neutraliza desde afuera para no
              tocar el componente, que en sus otros usos sí necesita ese aire. */}
          {contador && (
            <div className="shrink-0 rounded-2xl bg-black/[0.035] px-2 h-12 flex items-center [&>span]:mt-0">
              <ContadorImagenes
                valor={contador.valor}
                max={contador.max}
                onChange={contador.onChange}
              />
            </div>
          )}
          <button
            type="button"
            onClick={onGenerate}
            disabled={!!falta}
            className="flex-1 min-w-0 h-12 rounded-2xl bg-brand-primary text-white text-[15px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:pointer-events-none hover:opacity-90 transition-opacity"
          >
            {ctaLabel ??
              `Generar ${imagenesDeLaTanda} ${
                imagenesDeLaTanda === 1 ? 'imagen' : 'imágenes'
              }`}
            {creditCost != null && creditCost > 0 && (
              <span className="inline-flex items-center gap-1 bg-white text-brand-primary px-2 py-0.5 rounded-full text-[12.5px] font-semibold">
                <img
                  src="/images/token_icon.png"
                  alt=""
                  aria-hidden="true"
                  className="w-3.5 h-3.5 object-contain"
                />
                {creditCost}
              </span>
            )}
          </button>
        </div>
        {/* El estado, DEBAJO del botón: arriba lo empujaba hacia abajo cada vez que
            aparecía o desaparecía —el botón se movía bajo el cursor justo cuando se lo
            iba a apretar—. Acá el Generar se queda quieto y el mensaje se suma al pie.
            Es lo mismo que ya hace la barra de mobile. */}
        {mensaje && (
          <p className="text-[13px] text-brand-black/50 px-1">{mensaje}</p>
        )}
      </div>
    </div>
  );
};

export default PieConfiguracionModo;
